import { yupResolver } from "@hookform/resolvers/yup";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import FormInput from "shared/FormInput";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { iPickPackFee, PickPackFormData } from "./PickPackTypes";

interface PickPackFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PickPackFormData) => void;
  initialData?: iPickPackFee | null;
}

const PickPackFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: PickPackFormDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  // Form validation schema
  const PickPackFormSchema = yup.object().shape({
    name: yup
      .string()
      .required(t("pickPackFormDialog.validation.nameRequired")),
    description: yup
      .string()
      .required(t("pickPackFormDialog.validation.descriptionRequired")),
    min_items: yup
      .number()
      .required(t("pickPackFormDialog.validation.minItemsRequired"))
      .min(1, t("pickPackFormDialog.validation.minItemsPositive")),
    max_items: yup
      .number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? null : value
      )
      .test(
        "greater-than-min",
        t("pickPackFormDialog.validation.maxItemsGreater"),
        function (value) {
          const { min_items } = this.parent;
          if (value === null || value === undefined) return true;
          return value > min_items;
        }
      ),
    price: yup
      .number()
      .required(t("pickPackFormDialog.validation.priceRequired"))
      .min(0, t("pickPackFormDialog.validation.pricePositive")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PickPackFormData>({
    resolver: yupResolver(PickPackFormSchema),
    defaultValues: initialData || {
      name: "",
      description: "",
      min_items: 1,
      max_items: null,
      price: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        initialData || {
          name: "",
          description: "",
          min_items: 1,
          max_items: null,
          price: 0,
        }
      );
    }
  }, [initialData, reset, isOpen]);

  const handleFormSubmit = async (data: PickPackFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      handleClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({
      name: "",
      description: "",
      min_items: 1,
      max_items: null,
      price: 0,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>
              {initialData
                ? t("pickPackFormDialog.title.edit")
                : t("pickPackFormDialog.title.add")}
            </span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 flex flex-col h-full justify-start">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInput
              label={t("pickPackFormDialog.form.name")}
              name="name"
              register={register}
              errors={errors}
              placeholder={t("pickPackFormDialog.form.namePlaceholder")}
            />
            <FormInput
              label={t("pickPackFormDialog.form.description")}
              name="description"
              register={register}
              errors={errors}
              placeholder={t("pickPackFormDialog.form.descriptionPlaceholder")}
            />
            <FormInput
              label={t("pickPackFormDialog.form.minItems")}
              name="min_items"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("pickPackFormDialog.form.minItemsPlaceholder")}
            />
            <FormInput
              label={t("pickPackFormDialog.form.maxItems")}
              name="max_items"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("pickPackFormDialog.form.maxItemsPlaceholder")}
            />
            <FormInput
              label={t("pickPackFormDialog.form.price")}
              name="price"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("pickPackFormDialog.form.pricePlaceholder")}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("pickPackFormDialog.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {initialData
              ? t("pickPackFormDialog.buttons.update")
              : t("pickPackFormDialog.buttons.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Export component
export default PickPackFormDialog;
